import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import type { CashFlowTransaction } from 'app-components/flux/transactions/transactionsTypes';
import type React from 'react';
import DevMenuButton from 'components/DevMenuButton';
import QTip from 'components/QuickenControls/QTip';
import { dispatchRequestDialog } from 'components/RequestDialog/dispatcher';
import dispatchGeneratorDialog from 'components/Transactions/GeneratorDialog/dispatcher';
import { ConnectionStatusFooter } from 'components/ConnectionStatus';

import { useStyles } from '../styles';

type Props = {
  create: boolean;
  currentTxn: CashFlowTransaction;
  deleteQTip: string;
  fullDisableQBP: boolean;
  hideCreateRule?: boolean;
  /** Drawer layout hosts Delete inside the body, so the footer omits it and keeps Create rule / Update. */
  hideDelete?: boolean;
  completeEnough: boolean;
  isDirty: boolean;
  onDelete: () => void;
  onCreateRule: () => void;
  onSave: () => void;
};

export default function DialogActionsFooter({
  create,
  currentTxn,
  deleteQTip,
  fullDisableQBP,
  hideCreateRule,
  hideDelete,
  completeEnough,
  isDirty,
  onDelete,
  onCreateRule,
  onSave,
}: Props): React.JSX.Element {
  const { classes } = useStyles();

  return (
    <ConnectionStatusFooter>
      <DialogActions>
        {!create && !hideDelete && (
          <Box sx={{ flex: 1 }}>
            <QTip title={deleteQTip}>
              <div>
                <Button
                  id='delete-txn'
                  aria-label='delete-txn'
                  onClick={onDelete}
                  disabled={create || fullDisableQBP}
                  className={classes.deleteButton}
                >
                  Delete transaction
                </Button>
              </div>
            </QTip>
          </Box>
        )}

        <DevMenuButton>
          <MenuItem
            onClick={() =>
              dispatchRequestDialog({
                method: 'PUT',
                path: `/transactions/${currentTxn.id}`,
                body: JSON.stringify(currentTxn, null, 2),
              })
            }
          >
            Manual Transaction API Call
          </MenuItem>
          <MenuItem
            onClick={() =>
              dispatchGeneratorDialog({
                txns: [currentTxn],
                start: currentTxn.postedOn,
              })
            }
          >
            Generate Multiple Downloaded Transactions
          </MenuItem>
          <MenuItem
            onClick={() =>
              dispatchGeneratorDialog({
                txns: [
                  currentTxn.toJS(),
                  {
                    ...currentTxn.toJS(),
                    accountId: currentTxn.coa?.id,
                    coa: currentTxn.coa ? { ...currentTxn.coa, id: currentTxn.accountId } : null,
                    amount: -currentTxn.amount,
                  },
                ],
                start: currentTxn.postedOn,
              })
            }
          >
            Generate Multiple Transfer Transactions
          </MenuItem>
          <MenuItem
            onClick={() =>
              dispatchGeneratorDialog({
                txns: [currentTxn],
                start: currentTxn.postedOn,
                end: currentTxn.postedOn,
              })
            }
          >
            Generate 1 Downloaded Transaction
          </MenuItem>
        </DevMenuButton>

        {!hideCreateRule && (
          <Button
            type='submit'
            id='save-txn-create-rule'
            // Drawer: tertiary text action anchored left (Delete lives in the body); modal: existing filled style.
            variant={hideDelete ? 'text' : undefined}
            className={hideDelete ? undefined : classes.createRuleButton}
            sx={hideDelete ? { mr: 'auto', color: 'primary.main' } : undefined}
            onClick={onCreateRule}
            disabled={!completeEnough}
          >
            {isDirty ? 'Update & Create Rule' : 'Create Rule'}
          </Button>
        )}

        <Button
          type='submit'
          id='save-txn'
          variant='contained'
          disabled={!completeEnough || !isDirty}
          onClick={onSave}
          className={classes.updateButton}
        >
          {create ? 'Create' : 'Update'}
        </Button>
      </DialogActions>
    </ConnectionStatusFooter>
  );
}
